import { Box, Button, Stack } from '@mui/material';
import { ProjectsPageHeader } from 'src/pages/ProjectsPage/ProjectsPageHeader';
import { DEFAULT_SPACING } from 'src/theme/style-consts';
import { projectV2ApiSlice } from 'src/api/v2/project-v2-ApiSlice';
import { useAppSelector } from 'src/app/hooks';
import { ProjectsSortBy, ProjectsSortOrder } from 'src/api/projects-ui-slice';
import dayjs from 'dayjs';
import { SingleColumnPageBase } from "src/components/PageBase/SingleColumnPageBase";
import { ProjectsListView } from "src/pages/ProjectsPage/ProjectsListView";
import { ProjectsGridView } from "src/pages/ProjectsPage/ProjectsGridView";
import { PlusSvgIcon } from "src/pages/ProjectsPage/icons/PlusIcon";
import { ts } from "src/utils/translate";
import { ProjectWizard } from "src/pages/ProjectDetailsWizard/ProjectWizard";
import { useMatch, useNavigate, useParams } from "react-router";
import AppHeader from "src/components/AppHeader/AppHeader";
import { GetProjectResponseProject } from "src/model/api/v2/data-contracts";

const PROJECTS_HORIZONTAL_SPACING = '4rem';
const PROJECTS_HEADER_MIN_HEIGHT = '260px';

export function ProjectsPage(props: {}) {

  const getProjectsQuery = projectV2ApiSlice.useGetProjectsQuery();
  const projectsUiState = useAppSelector((state) => state.projectsUi);

  const navigate = useNavigate();

  const matchNew = useMatch("/projects/new");
  const matchEdit = useMatch("/projects/:projectId");

  const openProjectWizard = !!matchNew || !!matchEdit;

  const { projectId } = useParams();

  if (getProjectsQuery.data == null) {
    return (
        <AppHeader/>
    )
  }

  const numericId = Number(projectId);
  const editingProject = getProjectsQuery.data.projects.find(p => p.project.id === numericId);

  let projects = applyFilteringAndSorting(
      getProjectsQuery.data.projects,
      projectsUiState.searchText,
      projectsUiState.sortBy as ProjectsSortBy,
      projectsUiState.sortOrder,
      projectsUiState.startDate,
      projectsUiState.endDate
  );

  return (
      <>
        <SingleColumnPageBase
            pageContent={
              <>
                <Stack direction='column' spacing={DEFAULT_SPACING} sx={{pt: DEFAULT_SPACING, paddingX: PROJECTS_HORIZONTAL_SPACING, height: '100%', minHeight: 0}}>
                  <Stack sx={{ flexShrink: 0, minHeight: PROJECTS_HEADER_MIN_HEIGHT, justifyContent: 'space-evenly' }}>
                    <ProjectsPageHeader/>
                    <Box>
                      <Button
                          variant="contained" startIcon={<PlusSvgIcon/>}
                          onClick={() => {
                              navigate("/projects/new");
                          }
                      }>
                        {ts('create_new_project_button', 'Create New Project')}
                      </Button>
                    </Box>
                  </Stack>
                  {projectsUiState.displayMode === 'list' ? (
                      <Box sx={{ flex: 1, minHeight: 0 }}>
                        <ProjectsListView projects={projects}/>
                      </Box>
                  ) : (
                      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        <ProjectsGridView projects={projects}/>
                      </Box>
                  )}
                </Stack>
              </>
            }
        />

        <ProjectWizard
            open={openProjectWizard}
            onClose={() => {
                navigate("/projects");
            }}
            project={editingProject?.project ?? undefined}
            labels={editingProject?.labels ?? []}
        />
      </>
  )
}

function applyFilteringAndSorting(
    projects: GetProjectResponseProject[],
    searchText: string,
    sortBy: ProjectsSortBy,
    sortOrder: ProjectsSortOrder,
    startDate?: string | null,
    endDate?: string | null
): GetProjectResponseProject[] {

    const startDateDayJS = startDate ? dayjs(startDate).startOf('day') : null;
    const endDateDayJS = endDate ? dayjs(endDate).endOf('day') : null;

    return projects
        .filter((project) => {
            if (searchText === '') return true;

            const upperSearch = searchText.toUpperCase();

            const matchesName = project.project.project_name.toUpperCase().includes(upperSearch);

            const matchesLabel = project.labels?.some((label) =>
                label.name?.toUpperCase().includes(upperSearch)
            );

            const matchesAddress = project.project.street_address.toUpperCase().includes(upperSearch);

            const matchesCity = project.project.city.toUpperCase().includes(upperSearch);

            return matchesName || matchesLabel || matchesAddress || matchesCity;
        })
        .filter((project) => {
            const projDate = dayjs(project.project.created_at);

            if (startDateDayJS && projDate.isBefore(startDateDayJS)) return false;
            if (endDateDayJS && projDate.isAfter(endDateDayJS)) return false;

            return true;
        })
        .sort((project1, project2) => {
            let result = 0;

            if (sortBy === 'projectName') {
                const name1 = project1.project.project_name.toUpperCase();
                const name2 = project2.project.project_name.toUpperCase();
                result = name1.localeCompare(name2);
            } else if (sortBy === 'address') {
                const city1 = project1.project.city.toUpperCase();
                const city2 = project2.project.city.toUpperCase();
                result = city1.localeCompare(city2);
            } else if (sortBy === 'createdAt') {
                result = dayjs(project1.project.created_at).valueOf() - dayjs(project2.project.created_at).valueOf();
            } else if (sortBy === 'lastModified') {
                result = dayjs(project2.project.updated_at).valueOf() - dayjs(project1.project.updated_at).valueOf();
            }

            return sortOrder === 'desc' ? -result : result;
        });
}
